import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
	Box,
	Paper,
	Typography,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	IconButton,
	Tooltip,
	Grid,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Button as MuiButton,
} from '@mui/material';
import { Visibility as VisibilityIcon, Delete as DeleteIcon } from '@mui/icons-material';
import approveDenyLogo from '../../../assets/images/approve-deny-logo.png';
import { IManagerInitiationData } from '../interfaces/IOnboardingFlow';
import { ManagerInitiationForm } from '../components';
import style from './OnboardingDashboard.module.scss';
import {
	createOnboarding,
	getOnboardings,
	deleteOnboarding,
	OnboardingRecord,
} from 'core/API/onboarding';
import { syncUserJobCodes } from 'core/API/userJobCodes';
import { parseUserIdFromRef } from 'core/utils/checklistEventAssignment';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useUserData } from 'store/useUserData';
import {
	resolveOnboardingPermissions,
	canDeleteOnboardingRecord,
	canExecuteOnboardingRecord,
	canStartOnboarding,
	canViewOnboardingRecord,
	filterVisibleOnboardingRecords,
	getCurrentUserId,
} from '../utils/onboardingPermission';
import { SecurityRolePermission } from 'core/services/permissionService';
import { Button } from '../../../components/ui/button';
import EmployeeOnboardingReview from './EmployeeOnboardingReview';
import { capitalizeWords } from '../../../shared/utils/stringUtils';

interface OnboardingDashboardProps {
	workplaceId: string;
	organizationId: string;
}

// Helper functions to format display values
const formatPayType = (value: string): string => {
	if (!value) return value;
	const lowerValue = value.toLowerCase();
	if (lowerValue === 'full_time' || lowerValue === 'ft') return 'Full Time';
	if (lowerValue === 'part_time' || lowerValue === 'pt') return 'Part Time';
	return value; // Fallback to original value
};

const payTypeColumnSx = { whiteSpace: 'nowrap', width: '1%' };

const nameColumnSx = { whiteSpace: 'nowrap', minWidth: '200px', width: '18%' };

const emailColumnSx = {
	minWidth: '220px',
	width: '16%',
	maxWidth: '280px',
};

const emailCellContentSx = {
	display: 'block',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const phoneColumnSx = { whiteSpace: 'nowrap', minWidth: '150px', width: '12%' };

const formatEmploymentType = (value: string): string => {
	if (!value) return value;
	const lowerValue = value.toLowerCase();
	if (lowerValue === 'hourly') return 'Hourly';
	if (lowerValue === 'salary') return 'Salary';
	return value; // Fallback to original value
};

export default function OnboardingDashboard({
	workplaceId,
	organizationId: _organizationId,
}: OnboardingDashboardProps): JSX.Element {
	const [onboardingRecords, setOnboardingRecords] = useState<OnboardingRecord[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [currentView, setCurrentView] = useState<
		'dashboard' | 'create' | 'view' | 'employee-entry' | 'manager-review' | 'review-progress'
	>('dashboard');
	const [selectedRecord, setSelectedRecord] = useState<OnboardingRecord | null>(null);
	const [_totalItems, setTotalItems] = useState(0);
	const itemsPerPage = 30;
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [recordToDelete, setRecordToDelete] = useState<OnboardingRecord | null>(null);
	const [isDeletingRecord, setIsDeletingRecord] = useState(false);
	const { showToast } = useToastContext();

	// Permission checks for Onboarding module (All = manager scope, My = own archived record)
	const { currentUserRole } = useUserData();
	const onboardingPermissions = useMemo(
		() =>
			resolveOnboardingPermissions(
				(currentUserRole?.securityRolePermission ?? []) as SecurityRolePermission[],
			),
		[currentUserRole],
	);
	const currentUserId = useMemo(() => getCurrentUserId(), []);
	const canViewOnboarding = onboardingPermissions.canView;
	const canManageAllOnboardings = onboardingPermissions.canViewAll;
	const canStartNewOnboarding = canStartOnboarding(onboardingPermissions);
	const visibleOnboardingRecords = useMemo(
		() =>
			filterVisibleOnboardingRecords(
				onboardingRecords,
				onboardingPermissions,
				currentUserId,
			),
		[onboardingRecords, onboardingPermissions, currentUserId],
	);

	// Fetch onboarding records
	const fetchOnboardingRecords = useCallback(async () => {
		setIsLoading(true);
		try {
			const response: any = await getOnboardings(Number(workplaceId), 1, itemsPerPage);
			
			// Check if response has success: false (even with 200 status)
			if (response && response.success === false) {
				const errorMessage = response.message || response.error || 'Failed to fetch onboarding records';
				showToast('error', errorMessage);
				setOnboardingRecords([]);
				setTotalItems(0);
				return;
			}
			
			const records = response.member || response['hydra:member'] || [];
			const total = response.totalItems || response['hydra:totalItems'] || 0;

			setOnboardingRecords(records);
			setTotalItems(total);
		} catch (error: any) {
			console.error('Error fetching onboarding records:', error);
			const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch onboarding records';
			showToast('error', errorMessage);
			setOnboardingRecords([]);
			setTotalItems(0);
		} finally {
			setIsLoading(false);
		}
	}, [workplaceId, itemsPerPage, showToast]);

	useEffect(() => {
		if (!canViewOnboarding) {
			setOnboardingRecords([]);
			setTotalItems(0);
			return;
		}

		fetchOnboardingRecords();
	}, [canViewOnboarding, fetchOnboardingRecords]);

	// Handler for creating new onboarding
	const handleCreateOnboarding = () => {
		if (!canStartNewOnboarding) {
			showToast('error', 'You do not have permission to start onboarding');
			return;
		}

		setCurrentView('create');
		setSelectedRecord(null);
	};

	// Handler for submitting Phase 1 (Manager Initiation)
	const handleSubmitManagerInitiation = async (data: IManagerInitiationData) => {
		if (!canStartNewOnboarding) {
			showToast('error', 'You do not have permission to start onboarding');
			return;
		}

		try {
			// Map form data to API payload
			const payload = {
				user: data.existingEmployeeId || undefined, // Optional, for existing/rehire employees
				firstName: data.firstName,
				middleName: data.middleName || '',
				lastName: data.lastName,
				email: data.email,
				cellPhone: data.cellPhone,
				type: data.employeeType, // 'new', 'rehire', 'existing'
				workplace: `/api/workplaces/${workplaceId}`,
				employmentStartDate: new Date(data.employmentStartDate).toISOString(),
				payType: data.roleType, // 'part_time' or 'full_time'
				employmentType: data.employmentType, // 'hourly' or 'salary'
				securityRole: data.securityRole, // Already an IRI
				hierarchyAssignment: data.hierarchyAssignment, // Hierarchy IRI (required)
			};

			// Call API to create onboarding
			const response: any = await createOnboarding(payload);
			console.log('Onboarding created successfully:', response);

			// Check if response has success: false (even with 200 status)
			if (response && response.success === false) {
				const errorMessage = response.message || response.error || 'Failed to create onboarding record. Please try again.';
				showToast('error', errorMessage);
				return;
			}

			const createdUserId =
				parseUserIdFromRef(data.existingEmployeeId) ??
				parseUserIdFromRef(response?.user) ??
				parseUserIdFromRef(response?.data?.user) ??
				parseUserIdFromRef(response?.userId);

			const jobCodeIds = data.eligibleJobCodes || [];
			if (createdUserId != null && jobCodeIds.length > 0) {
				try {
					await syncUserJobCodes(createdUserId, jobCodeIds);
				} catch (jobCodeError: any) {
					console.error('Onboarding created but failed to assign job codes:', jobCodeError);
					showToast(
						'error',
						jobCodeError?.message ||
							'Onboarding created, but Job Code assignment failed. Assign them under User → Availability.',
					);
					await fetchOnboardingRecords();
					setCurrentView('dashboard');
					return;
				}
			}

			showToast('success', 'Onboarding created successfully!');

			// Refresh the list
			await fetchOnboardingRecords();

			// Return to dashboard
			setCurrentView('dashboard');
		} catch (error: any) {
			console.error('Error creating onboarding record:', error);
			showToast(
				'error',
				error?.response?.data?.message || 'Failed to create onboarding record. Please try again.'
			);
		}
	};

	// Handler for opening a record for review/edit
	const handleOpenRecord = (record: OnboardingRecord) => {
		if (!canViewOnboardingRecord(onboardingPermissions, record, currentUserId)) {
			showToast('error', 'You do not have permission to view this onboarding record');
			return;
		}

		setSelectedRecord(record);
		setCurrentView('review-progress');
	};

	const handleDeleteClick = (record: OnboardingRecord) => {
		if (!canDeleteOnboardingRecord(onboardingPermissions, record, currentUserId)) {
			showToast('error', 'You do not have permission to delete this onboarding record');
			return;
		}

		setRecordToDelete(record);
		setDeleteDialogOpen(true);
	};

	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
		setRecordToDelete(null);
	};

	const handleDeleteConfirm = async () => {
		if (!recordToDelete) return;

		setIsDeletingRecord(true);
		try {
			await deleteOnboarding(recordToDelete.id);
			showToast('success', 'Onboarding record deleted successfully');
			await fetchOnboardingRecords();
			setDeleteDialogOpen(false);
			setRecordToDelete(null);
		} catch (error: any) {
			console.error('Error deleting onboarding record:', error);
			const errorMessage =
				error?.response?.data?.message || error?.message || 'Failed to delete onboarding record';
			showToast('error', errorMessage);
		} finally {
			setIsDeletingRecord(false);
		}
	};

	// Handler for canceling form
	const handleCancelForm = () => {
		setCurrentView('dashboard');
		setSelectedRecord(null);
	};

	// Format date for display
	const formatDate = (dateString: string | undefined) => {
		if (!dateString) return 'N/A';
		try {
			return new Date(dateString).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			});
		} catch {
			return 'Invalid Date';
		}
	};

	return (
		<div className={style.DashboardContainer}>
			{!canViewOnboarding && (
				<Paper sx={{ p: 3 }}>
					<Typography variant="h6" sx={{ color: 'rgb(68, 99, 136)' }}>
						Access denied
					</Typography>
					<Typography sx={{ mt: 1 }}>
						You need Onboarding view permission to access this module.
					</Typography>
				</Paper>
			)}

			{canViewOnboarding && (
				<>
			{/* Header - only show on dashboard view */}
			{currentView === 'dashboard' && canStartNewOnboarding && (
				<div className={style.Header}>
					<Button onClick={handleCreateOnboarding} className={style.CreateButton}>
						Start Onboarding
					</Button>
				</div>
			)}

			{/* Dashboard View */}
			{currentView === 'dashboard' && (
				<>
					{isLoading ? (
						<Box sx={{ textAlign: 'center', padding: '3rem' }}>
							<CircularProgress />
						</Box>
					) : (
						<>
							{/* Phase 1 & 2: manager scope only (Onboarding - All) */}
							{canManageAllOnboardings && (
							<Paper sx={{ p: 3, mb: 3 }}>
								<Box sx={{ mb: 2 }}>
									<Typography variant="h6" sx={{ color: 'rgb(68, 99, 136)' }}>
										Phase 1: Manager Initiated - Waiting for Employee (
										{
											visibleOnboardingRecords.filter((r) => r.phase1CompletedAt && !r.phase2CompletedAt)
												.length
										}
										)
									</Typography>
								</Box>
								{visibleOnboardingRecords.filter((r) => r.phase1CompletedAt && !r.phase2CompletedAt)
									.length === 0 ? (
									<Box sx={{ textAlign: 'center', padding: '2rem' }}>
										<Typography sx={{ color: '#999' }}>No records in Phase 1</Typography>
									</Box>
								) : (
									<TableContainer
										sx={{
											maxHeight: 'calc(100vh - 300px)',
											'&::-webkit-scrollbar': {
												width: '4px',
												height: '4px',
											},
											'&::-webkit-scrollbar-track': {
												background: '#f1f1f1',
												borderRadius: '2px',
											},
											'&::-webkit-scrollbar-thumb': {
												background: '#888',
												borderRadius: '2px',
												'&:hover': {
													background: '#555',
												},
											},
										}}
									>
										<Table stickyHeader>
											<TableHead>
												<TableRow>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', ...nameColumnSx }}>
														Name
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', ...emailColumnSx }}>
														Email
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', ...phoneColumnSx }}>
														Phone
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Type
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Status
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Employment Start
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', ...payTypeColumnSx }}>
														Pay Type
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Employment Type
													</TableCell>
													<TableCell
														sx={{
															fontWeight: 'bold',
															backgroundColor: '#f5f5f5',
															textAlign: 'center',
															width: '120px',
															minWidth: '120px',
														}}
													>
														Actions
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{visibleOnboardingRecords
													.filter((r) => r.phase1CompletedAt && !r.phase2CompletedAt)
													.map((record) => (
														<TableRow
															key={record.id}
															sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
														>
															<TableCell sx={nameColumnSx}>
																{`${record.firstName} ${record.middleName ? record.middleName + ' ' : ''}${record.lastName}`}
															</TableCell>
															<TableCell sx={emailColumnSx}>
																<Tooltip title={record.email}>
																	<Box component="span" sx={emailCellContentSx}>
																		{record.email}
																	</Box>
																</Tooltip>
															</TableCell>
															<TableCell sx={phoneColumnSx}>{record.cellPhone}</TableCell>
															<TableCell>
															{capitalizeWords(record.type)}
														</TableCell>
														<TableCell>
															{capitalizeWords(record.status)}
															</TableCell>
															<TableCell>{formatDate(record.employmentStartDate)}</TableCell>
														<TableCell sx={payTypeColumnSx}>{formatPayType(record.payType)}</TableCell>
														<TableCell>{formatEmploymentType(record.employmentType)}</TableCell>
															<TableCell
																sx={{ textAlign: 'center', width: '120px', minWidth: '120px' }}
															>
																<Tooltip title="View Details">
																	<IconButton
																		size="small"
																		onClick={() => handleOpenRecord(record)}
																		sx={{ color: 'rgb(68, 99, 136)' }}
																	>
																		<VisibilityIcon fontSize="small" />
																	</IconButton>
																</Tooltip>
																{canDeleteOnboardingRecord(
																	onboardingPermissions,
																	record,
																	currentUserId,
																) && (
																	<Tooltip title="Delete">
																		<IconButton
																			size="small"
																			onClick={() => handleDeleteClick(record)}
																			sx={{ color: '#d32f2f' }}
																		>
																			<DeleteIcon fontSize="small" />
																		</IconButton>
																	</Tooltip>
																)}
															</TableCell>
														</TableRow>
													))}
											</TableBody>
										</Table>
									</TableContainer>
								)}
							</Paper>
							)}

							{canManageAllOnboardings && (
							<Paper sx={{ p: 3, mb: 3 }}>
								<Box sx={{ mb: 2 }}>
									<Typography variant="h6" sx={{ color: 'rgb(68, 99, 136)' }}>
										Phase 2: Ready for Manager Review (
										{
											visibleOnboardingRecords.filter((r) => r.phase2CompletedAt && (!r.phase2ReviewedAt || r.phase2ReviewStatus === 'pending_approval'))
												.length
										}
										)
									</Typography>
								</Box>
							{visibleOnboardingRecords.filter((r) => r.phase2CompletedAt && (!r.phase2ReviewedAt || r.phase2ReviewStatus === 'pending_approval'))
									.length === 0 ? (
									<Box sx={{ textAlign: 'center', padding: '2rem' }}>
										<Typography sx={{ color: '#999' }}>
											No records awaiting manager review
										</Typography>
									</Box>
								) : (
									<TableContainer
										sx={{
											maxHeight: 'calc(100vh - 300px)',
											'&::-webkit-scrollbar': {
												width: '4px',
												height: '4px',
											},
											'&::-webkit-scrollbar-track': {
												background: '#f1f1f1',
												borderRadius: '2px',
											},
											'&::-webkit-scrollbar-thumb': {
												background: '#888',
												borderRadius: '2px',
												'&:hover': {
													background: '#555',
												},
											},
										}}
									>
										<Table stickyHeader>
											<TableHead>
												<TableRow>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', ...nameColumnSx }}>
														Name
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', ...emailColumnSx }}>
														Email
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', ...phoneColumnSx }}>
														Phone
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Type
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Status
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Employment Start
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', ...payTypeColumnSx }}>
														Pay Type
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Employment Type
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Review Status
													</TableCell>
													<TableCell
														sx={{
															fontWeight: 'bold',
															backgroundColor: '#f5f5f5',
															textAlign: 'center',
															width: '120px',
															minWidth: '120px',
														}}
													>
														Actions
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{visibleOnboardingRecords
													.filter((r) => r.phase2CompletedAt && (!r.phase2ReviewedAt || r.phase2ReviewStatus === 'pending_approval'))
													.map((record) => (
														<TableRow
															key={record.id}
															sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
														>
															<TableCell sx={nameColumnSx}>
																{`${record.firstName} ${record.middleName ? record.middleName + ' ' : ''}${record.lastName}`}
															</TableCell>
															<TableCell sx={emailColumnSx}>
																<Tooltip title={record.email}>
																	<Box component="span" sx={emailCellContentSx}>
																		{record.email}
																	</Box>
																</Tooltip>
															</TableCell>
															<TableCell sx={phoneColumnSx}>{record.cellPhone}</TableCell>
															<TableCell>
															{capitalizeWords(record.type)}
														</TableCell>
														<TableCell>
															{capitalizeWords(record.status)}
															</TableCell>
															<TableCell>{formatDate(record.employmentStartDate)}</TableCell>
															<TableCell sx={payTypeColumnSx}>
																{formatPayType(record.payType)}</TableCell>
															<TableCell>{formatEmploymentType(record.employmentType)}</TableCell>{' '}
															<TableCell>
																{record.phase2ReviewStatus ? capitalizeWords(record.phase2ReviewStatus) : 'Pending'}
															</TableCell>{' '}
															<TableCell
																sx={{ textAlign: 'center', width: '120px', minWidth: '120px' }}
															>
																<Tooltip title="Review & Approve/Deny">
																	<IconButton
																		size="small"
																		onClick={() => handleOpenRecord(record)}
																		sx={{ padding: '4px' }}
																	>
																		<img 
																			src={approveDenyLogo} 
																			alt="Review & Approve/Deny" 
																			style={{ width: '30px', height: '30px', display: 'block' }}
																		/>
																	</IconButton>
																</Tooltip>
															</TableCell>
														</TableRow>
													))}
											</TableBody>
										</Table>
									</TableContainer>
								)}
							</Paper>
							)}

							{/* Phase 3: Archive — My users see only their own completed onboarding */}
							<Paper sx={{ p: 3, mb: 3 }}>
								<Box sx={{ mb: 2 }}>
									<Typography variant="h6" sx={{ color: 'rgb(68, 99, 136)' }}>
										Phase 3: Manager Reviewed - Archive (
									{visibleOnboardingRecords.filter((r) => r.phase2ReviewedAt && r.phase2ReviewStatus !== 'pending_approval').length})
									</Typography>
								</Box>
								{visibleOnboardingRecords.filter((r) => r.phase2ReviewedAt && r.phase2ReviewStatus !== 'pending_approval').length === 0 ? (
									<Box sx={{ textAlign: 'center', padding: '2rem' }}>
										<Typography sx={{ color: '#999' }}>No completed onboarding records</Typography>
									</Box>
								) : (
									<TableContainer
										sx={{
											maxHeight: 'calc(100vh - 300px)',
											'&::-webkit-scrollbar': {
												width: '4px',
												height: '4px',
											},
											'&::-webkit-scrollbar-track': {
												background: '#f1f1f1',
												borderRadius: '2px',
											},
											'&::-webkit-scrollbar-thumb': {
												background: '#888',
												borderRadius: '2px',
												'&:hover': {
													background: '#555',
												},
											},
										}}
									>
										<Table stickyHeader>
											<TableHead>
												<TableRow>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', ...nameColumnSx }}>
														Name
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', ...emailColumnSx }}>
														Email
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', ...phoneColumnSx }}>
														Phone
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Type
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Status
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Employment Start
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', ...payTypeColumnSx }}>
														Pay Type
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Employment Type
													</TableCell>{' '}
													<TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
														Review Status
													</TableCell>{' '}
													<TableCell
														sx={{
															fontWeight: 'bold',
															backgroundColor: '#f5f5f5',
															textAlign: 'center',
															width: '120px',
															minWidth: '120px',
														}}
													>
														Actions
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{visibleOnboardingRecords
													.filter((r) => r.phase2ReviewedAt)
													.map((record) => (
														<TableRow
															key={record.id}
															sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
														>
															<TableCell sx={nameColumnSx}>
																{`${record.firstName} ${record.middleName ? record.middleName + ' ' : ''}${record.lastName}`}
															</TableCell>
															<TableCell sx={emailColumnSx}>
																<Tooltip title={record.email}>
																	<Box component="span" sx={emailCellContentSx}>
																		{record.email}
																	</Box>
																</Tooltip>
															</TableCell>
															<TableCell sx={phoneColumnSx}>{record.cellPhone}</TableCell>
															<TableCell>
																{capitalizeWords(record.type)}
															</TableCell>
															<TableCell>
																{capitalizeWords(record.status)}
															</TableCell>
															<TableCell>{formatDate(record.employmentStartDate)}</TableCell>
															<TableCell sx={payTypeColumnSx}>
																{formatPayType(record.payType)}</TableCell>
															<TableCell>{formatEmploymentType(record.employmentType)}</TableCell>{' '}
															<TableCell>
																{record.phase2ReviewStatus ? capitalizeWords(record.phase2ReviewStatus) : 'Pending'}
															</TableCell>{' '}
															<TableCell
																sx={{ textAlign: 'center', width: '120px', minWidth: '120px' }}
															>
																<Tooltip title="View Details">
																	<IconButton
																		size="small"
																		onClick={() => handleOpenRecord(record)}
																		sx={{ color: 'rgb(68, 99, 136)' }}
																	>
																		<VisibilityIcon fontSize="small" />
																	</IconButton>
																</Tooltip>
															</TableCell>
														</TableRow>
													))}
											</TableBody>
										</Table>
									</TableContainer>
								)}
							</Paper>
						</>
					)}
				</>
			)}

			{/* Phase 1: Manager Initiation Form */}
			{currentView === 'create' && (
				<ManagerInitiationForm
					workplaceId={workplaceId}
					onSubmit={handleSubmitManagerInitiation}
					onCancel={handleCancelForm}
				/>
			)}

			{/* Employee Onboarding Progress Review */}
			{currentView === 'review-progress' && selectedRecord && (
				<EmployeeOnboardingReview
					record={selectedRecord}
					onBack={handleCancelForm}
					onRefresh={fetchOnboardingRecords}
					canExecuteOnboarding={canExecuteOnboardingRecord(
						onboardingPermissions,
						selectedRecord,
						currentUserId,
					)}
					canDeleteOnboarding={canDeleteOnboardingRecord(
						onboardingPermissions,
						selectedRecord,
						currentUserId,
					)}
				/>
			)}

			{/* View Onboarding Details (deprecated - use review-progress instead) */}
			{currentView === 'view' && selectedRecord && (
				<div className={style.DashboardContainer}>
					<Grid container direction="column" spacing={2} sx={{ pl: 2, pt: 2 }}>
						<Grid container direction="row" justifyContent="space-between">
							<Grid>
								<Button className={style.BackButton} onClick={handleCancelForm}>
									Back
								</Button>
							</Grid>
						</Grid>
						<Grid container direction="row" justifyContent="flex-start" alignItems="center">
							<h2 className={style.formTitle}>Onboarding Details</h2>
						</Grid>
					</Grid>

					<Paper sx={{ p: 3, m: 2, mt: 1 }}>
						<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
							<Box>
								<Typography variant="subtitle2" color="text.secondary">
									Full Name
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{`${selectedRecord.firstName} ${selectedRecord.middleName ? selectedRecord.middleName + ' ' : ''}${selectedRecord.lastName}`}
								</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">
									Status
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>{capitalizeWords(selectedRecord.status)}</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">
									Email
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{selectedRecord.email}
								</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">
									Cell Phone
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{selectedRecord.cellPhone}
								</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">
									Employee Type
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>{selectedRecord.type}</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">
									Employment Start Date
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{formatDate(selectedRecord.employmentStartDate)}
								</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">
									Pay Type
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>{formatPayType(selectedRecord.payType)}</Typography>
							</Box>

							<Box>
								<Typography variant="subtitle2" color="text.secondary">
									Employment Type
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>{formatEmploymentType(selectedRecord.employmentType)}</Typography>
							</Box>
						</Box>
					</Paper>
				</div>
			)}

			{/* Phase 2 & 3: To be implemented with new API structure */}
			{/* {currentView === 'employee-entry' && selectedRecord && (
				<EmployeeDataEntryView
					record={selectedRecord}
					onClose={handleCancelForm}
				/>
			)} */}

			{/* {currentView === 'manager-review' && selectedRecord && (
				<ManagerReviewView
					record={selectedRecord}
					onComplete={async () => {
						await fetchOnboardingRecords();
						setCurrentView('dashboard');
						setSelectedRecord(null);
					}}
					onCancel={handleCancelForm}
				/>
			)} */}
				</>
			)}

			<Dialog
				open={deleteDialogOpen}
				onClose={handleDeleteCancel}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description"
				maxWidth="sm"
				fullWidth
				style={{ zIndex: 9999 }}
				PaperProps={{
					style: {
						backgroundColor: 'white',
						boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
						borderRadius: '8px',
						minWidth: '400px',
					},
				}}
			>
				<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
				<DialogContent>
					<DialogContentText id="delete-dialog-description">
						Are you sure you wish to cancel and delete the onboarding for this employee?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<MuiButton
						onClick={handleDeleteCancel}
						style={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: 'white',
							textTransform: 'none',
							borderRadius: '4px',
							padding: '5px 15px',
						}}
						disabled={isDeletingRecord}
					>
						Cancel
					</MuiButton>
					<MuiButton
						style={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: 'white',
							textTransform: 'none',
							borderRadius: '4px',
							padding: '5px 15px',
						}}
						onClick={handleDeleteConfirm}
						disabled={isDeletingRecord}
					>
						{isDeletingRecord ? 'Deleting...' : 'Delete'}
					</MuiButton>
				</DialogActions>
			</Dialog>
		</div>
	);
}

